import { Button } from "components/Button/Button";
import Placeholder from "components/Layout/Placeholder";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import RecordLabel from "components/RecordLabel";
import RecordTable from "components/Table/RecordTable";
import { TableColumn } from "components/Table/tableUtils";
import { FeConfigType } from "config/config";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getRelativeUrl } from "utils/service";
import { startCase } from "lodash";
import { zoneGraphql } from "../ZoneTagList/zoneGraphql";
import "./ZoneMemberList.scss";

interface MemberRow {
    id: string;
    recordId: string;
    name: string;
    recordType: string;
}

interface RecordZoneMember {
    id: string;
    record_id: string;
    record_id_record?: { name?: string | null; record_type?: string | null } | null;
}

/**
 * "Records in zone" tab on a Permission zone screen (RBAC Model G).
 *
 * Lists the records tagged directly into the zone and lets the user take one
 * out. Records are added from their own screen (lock button in the header).
 */
export const ZoneMemberList = ({ zoneId }: { zoneId: string }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [rows, setRows] = useState<MemberRow[]>([]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await zoneGraphql(
                `query ($filter: String) {
                    members: record_zone_plain_list(filter: $filter, limit: 500) {
                        id
                        record_id
                        record_id_record { name record_type }
                    }
                }`,
                { filter: JSON.stringify({ field: "zone_id", op: "eq", value: zoneId }) },
            );
            const members = ((data?.members ?? []) as RecordZoneMember[])
                .map((tag) => ({
                    id: tag.id,
                    recordId: tag.record_id,
                    name: tag.record_id_record?.name ?? tag.record_id,
                    recordType: (tag.record_id_record?.record_type ?? "").toLowerCase(),
                }))
                .sort((a, b) => a.name.localeCompare(b.name));
            setRows(members);
        } catch (error: any) {
            NotificationService.error(
                <RestErrorNotification
                    titleOverride="Sorry, couldn't load the records in this zone"
                    error={error}
                />,
            );
        } finally {
            setIsLoading(false);
        }
    }, [zoneId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const removeTag = useCallback(
        async (tagId: string) => {
            setIsSaving(true);
            try {
                await zoneGraphql(`mutation ($id: String!) { delete_record_zone(id: $id) }`, {
                    id: tagId,
                });
                await fetchData();
            } catch (error: any) {
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Sorry, couldn't remove the record from the zone"
                        error={error}
                    />,
                );
            } finally {
                setIsSaving(false);
            }
        },
        [fetchData],
    );

    const columns: TableColumn[] = useMemo(
        () => [
            {
                label: "Name",
                key: "name",
                shown: true,
                sortable: false,
                dto: (row: MemberRow) => (
                    <RecordLabel
                        link={getRelativeUrl(row.recordType as FeConfigType, row.recordId)}
                        name={row.name}
                    />
                ),
            },
            {
                label: "Type",
                key: "recordType",
                shown: true,
                sortable: false,
                dto: (row: MemberRow) => startCase(row.recordType),
            },
            {
                label: "Actions",
                key: "actions",
                shown: true,
                sortable: false,
                dto: (row: MemberRow) => (
                    <Button
                        variation="clear"
                        size="small"
                        label="Remove from zone"
                        disabled={isSaving}
                        onClick={() => removeTag(row.id)}
                    />
                ),
            },
        ],
        [isSaving, removeTag],
    );

    return (
        <div className="zoneMemberList">
            <p className="zoneMemberHint">
                Records tagged into this zone. Everything inside a tagged project, bundle or object
                template is in the zone too. To add a record, open it and use the lock button in its
                header.
            </p>
            {isLoading ? (
                <Placeholder.List />
            ) : rows.length === 0 ? (
                <p className="zoneMemberHint">No records are tagged into this zone yet.</p>
            ) : (
                <RecordTable
                    columns={columns}
                    data={rows}
                    totalItemCount={rows.length}
                    recordTypes="all"
                    isLoading={false}
                    tableSettingsKey="zoneMemberList"
                    options={{
                        toolbar: "none",
                        toolbarFilters: false,
                        selectableRows: "none",
                        showSearch: false,
                        viewColumns: false,
                        jumpToPage: false,
                        pagination: false,
                        density: "high",
                    }}
                />
            )}
        </div>
    );
};
